import { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";

import { DEFAULT_LANGUAGE, WEGLOT_KEY } from "./utils/constants";
import { AppRouter } from "./routes/AppRouter";
import { LoaderProvider } from "./context/LoaderContext";

import "../src/assets/css/colors.css";
import "../src/assets/css/styles.css";
import "../src/assets/js/main.js";
import '../src/assets/css/style.min.css';
import '../src/assets/css/fonts.css';

const App = () => {
  const [lang, setLang] = useState<string | null>(null);
  const [isWeglotReady, setIsWeglotReady] = useState(false);

  const getLanguage = (): string => {
    try {
      const storedLanguage = localStorage.getItem("selectedLang");
      return storedLanguage || DEFAULT_LANGUAGE;
    } catch (error) {
      return DEFAULT_LANGUAGE;
    }
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.weglot.com/weglot.min.js";
    script.async = true;

    script.onload = () => {
      if (window.Weglot) {
        // Add delay before initializing
        const delay = 500; // 1 second delay (tune as needed)

        const timer = setTimeout(() => {
          window.Weglot.initialize({
            api_key: WEGLOT_KEY,
            originalLanguage: getLanguage(),
            destinationLanguages: ["es", "nl", "sv"],
          });

          window.Weglot.on("initialized", () => {
            // console.log("Weglot initialized");
            setIsWeglotReady(true);

            const storedLanguage = getLanguage();
            if (storedLanguage && storedLanguage !== DEFAULT_LANGUAGE) {
              window.Weglot.switchTo(storedLanguage);
            }
          });

          window.Weglot.on("languageChanged", (newLang: string) => {
            //console.log("Language changed →", newLang);
            localStorage.setItem("selectedLang", newLang);
            setLang(newLang);
          });
        }, delay);

        // cleanup
        return () => clearTimeout(timer);
      }
    };

    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const storedLanguage = localStorage.getItem("selectedLang");
      if (storedLanguage) {
        setLang(storedLanguage);
        clearInterval(interval);
      }
    }, 1000); // check every second
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!lang || !isWeglotReady || !window.Weglot) return;
    try {
      const userLang = getLanguage();
      const current = window.Weglot.getCurrentLang();

      if (current !== userLang) {
        // console.log("Switching Weglot to user language:", userLang);
        window.Weglot.switchTo(userLang);
        localStorage.setItem("selectedLang", userLang);
      }

    } catch (err) {
      console.error("Weglot switch error:", err);
    }
  }, [lang, isWeglotReady]);

  return (
    <>  
      <LoaderProvider>
        <AppRouter />
      </LoaderProvider>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default App;
